import type { Accessor, Component } from 'solid-js'
import { For, Show } from 'solid-js'
import type { EventItem, EventsViewType } from '../../../features/engagement/domain/models/events'
import Table, { type TableColumn, TableStatus } from './Table'
import TablePagination from './TablePagination'
import { Icon } from '@iconify-icon/solid'
import { Trans } from '@mbarzda/solid-i18next'
import EventsTableRow from './EventsTableRow'

interface EventsTableProps {
  items: EventItem[]
  totalItems: number
  viewType: EventsViewType
  page: Accessor<number>
  pageSize: number
  onPageChange: (page: number) => void
  onSort?: (column: string, direction: 'asc' | 'desc') => void
  sortColumn: Accessor<string | null>
  sortDirection: Accessor<'asc' | 'desc'>
  isLoading?: boolean
}

const EventsTable: Component<EventsTableProps> = (props) => {
  const startIndex = () => (props.page() - 1) * props.pageSize

  const tableOrderBy = () => {
    const column = props.sortColumn()
    if (!column) {
      return undefined
    }
    return `${column}:${props.sortDirection()}`
  }

  const setTableOrderBy = (orderBy: string | undefined): void => {
    // The Table component cycles desc -> asc -> undefined. In this screen we keep desc -> asc -> desc.
    if (!orderBy) {
      const currentColumn = props.sortColumn()
      if (currentColumn) {
        props.onSort?.(currentColumn, 'desc')
      }
      return
    }

    const [column, direction] = orderBy.split(':')
    if (column && (direction === 'asc' || direction === 'desc')) {
      props.onSort?.(column, direction)
    }
  }

  const tableColumns: TableColumn[] = [
    {
      id: 'index',
      class: 'w-12 text-primary-600',
      header: (
        <Show when={props.viewType === 'cities'} fallback={<Icon icon="heroicons:ticket" class="text-[1.25rem]" />}>
          <Icon icon="heroicons:map-pin" class="text-[1.25rem]" />
        </Show>
      )
    },
    {
      id: 'city_or_event',
      class: 'text-primary-600',
      header: (
        <Show when={props.viewType === 'cities'} fallback={<Trans key="ls_events_table_event" />}>
          <Trans key="ls_events_table_city" />
        </Show>
      )
    },
    {
      id: 'buy_now_clicks',
      sortable: true
    },
    {
      id: 'presale_codes',
      sortable: true
    },
    {
      id: 'meet_and_greet',
      sortable: true
    },
    {
      id: 'vip',
      sortable: true
    },
    {
      id: 'rsvp',
      sortable: true
    }
  ]

  const status = () => {
    if (props.isLoading && props.items.length === 0) {
      return TableStatus.Init
    }
    if (props.isLoading) {
      return TableStatus.Loading
    }
    if (props.items.length === 0) {
      return TableStatus.NoData
    }
    return TableStatus.Loaded
  }

  return (
    <div class="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div class="border-t border-gray-200">
        <div class="overflow-x-auto">
          <Table
            status={status}
            columns={tableColumns}
            orderBy={tableOrderBy}
            setOrderBy={setTableOrderBy}
            tableId="events"
          >
            <For each={props.items}>
              {(item, index) => (
                <EventsTableRow item={item} rowNumber={startIndex() + index() + 1} viewType={props.viewType} />
              )}
            </For>
          </Table>
        </div>
        <Show when={props.totalItems > props.pageSize}>
          <TablePagination
            totalCount={props.totalItems}
            pageSize={props.pageSize}
            page={props.page}
            setPage={props.onPageChange}
          />
        </Show>
      </div>
    </div>
  )
}

export default EventsTable
